import { ArrowRight, User, Server, Database, HardDrive, Cpu, Mail, Cloud } from 'lucide-react';

/**
 * Inline data flow diagram for the public Security page.
 * SVG-free, all CSS — describes where customer data actually goes.
 */
export default function DataFlowDiagram() {
  const node = (Icon, title, sub, tone = 'default') => {
    const tones = {
      default: 'border-[#E6E0D9] bg-white text-[#2C2420]',
      primary: 'border-[#4A3B32] bg-[#4A3B32] text-white',
      mongo: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      storage: 'border-amber-200 bg-amber-50 text-amber-900',
      ai: 'border-purple-200 bg-purple-50 text-purple-900',
      email: 'border-blue-200 bg-blue-50 text-blue-900',
      cloud: 'border-sky-200 bg-sky-50 text-sky-900',
    };
    return (
      <div className={`flex items-start gap-3 p-4 rounded-xl border ${tones[tone]} shadow-sm`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone === 'primary' ? 'bg-white/15' : 'bg-white'}`}>
          <Icon className={`w-5 h-5 ${tone === 'primary' ? 'text-white' : ''}`} />
        </div>
        <div>
          <p className="text-sm font-medium leading-tight">{title}</p>
          <p className={`text-xs mt-0.5 ${tone === 'primary' ? 'text-white/80' : 'opacity-70'}`}>{sub}</p>
        </div>
      </div>
    );
  };

  const arrow = (label) => (
    <div className="flex flex-col items-center justify-center text-[#8C8078] text-[10px] gap-0.5 px-1">
      <ArrowRight className="w-4 h-4" />
      {label && <span className="text-center leading-tight max-w-[110px]">{label}</span>}
    </div>
  );

  return (
    <section className="py-16 bg-white border-b border-[#E6E0D9]" data-testid="data-flow-diagram">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="font-heading text-2xl text-[#2C2420] mb-2">Where your data goes</h2>
        <p className="text-[#5D524D] mb-8 max-w-3xl">
          A single look at every system that touches customer data on QIndex One. Each route is
          authenticated, encrypted in transit (TLS&nbsp;1.3) and at rest (AES&#8209;256).
        </p>

        {/* Primary horizontal flow */}
        <div className="rounded-2xl border border-[#E6E0D9] bg-[#FAFAF9] p-6">
          <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-4">Request path</p>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-1">
            {node(User, 'You', 'Browser session', 'default')}
            {arrow('TLS 1.3')}
            {node(Server, 'QIndex One Backend', 'FastAPI on Emergent Labs', 'primary')}
            {arrow('JWT + RBAC')}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
              {node(Database, 'Managed MongoDB', 'Account, client, task, link, folder, doc metadata, audit logs', 'mongo')}
              {node(HardDrive, 'Managed Object Storage', 'Uploaded document files (qindex-one/clients/{id}/...)', 'storage')}
            </div>
          </div>

          {/* Optional integrations row */}
          <div className="border-t border-[#E6E0D9] mt-8 pt-6">
            <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-4">On-demand integrations</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {node(Cpu, 'Managed AI Gateway', 'Gemini · GPT · Claude — no training on your data, audit-logged, org-toggleable', 'ai')}
              {node(Mail, 'Resend', 'Transactional email — portal invites, password resets, weekly digests', 'email')}
              {node(Cloud, 'Cloud File Picker SDKs', 'Dropbox · Google Drive · OneDrive — loaded only when you click "Link from cloud"; file contents are not transmitted to QIndex One', 'cloud')}
            </div>
          </div>
        </div>

        <p className="text-xs text-[#8C8078] mt-4">
          The full Sub-processors document, with provider names, role, data category and region for each row above, is{' '}
          <a href="/api/legal/policies/subprocessors/pdf" className="text-[#4A3B32] underline hover:text-[#2C2420]" data-testid="data-flow-subprocessors-link">downloadable as a PDF</a>.
        </p>
      </div>
    </section>
  );
}
